import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Calendar, Plus, Loader2, Search, Send, CheckCircle, XCircle, Trash2, Clock, BellRing, MessageCircle, LayoutGrid, List, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { api, fmtErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import ImportDialog from "@/components/ImportDialog";

const STATUS_STYLES = {
    scheduled: "bg-blue-50 text-blue-700 border-blue-200",
    completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
    cancelled: "bg-slate-100 text-slate-500 border-slate-200",
};

function notifyToast(r) {
    const parts = [];
    parts.push(r.email === "sent" ? "Email sent" : r.email === "no_email_on_record" ? "No email on record" : "Email failed");
    parts.push(r.whatsapp === "sent" ? "WhatsApp sent" : r.whatsapp === "not_configured" ? "WhatsApp not configured (add Meta keys)" : "WhatsApp failed");
    if (r.email === "sent" || r.whatsapp === "sent") toast.success(parts.join(" · "));
    else toast.warning(parts.join(" · "));
}

export default function Appointments() {
    const { user } = useAuth();
    const [params, setParams] = useSearchParams();
    const today = new Date().toISOString().slice(0, 10);
    const [date, setDate] = useState(today);
    const [view, setView] = useState("list");
    const [month, setMonth] = useState(() => { const d = new Date(); d.setDate(1); return d; });
    const [monthAppts, setMonthAppts] = useState({});
    const [appts, setAppts] = useState(null);
    const [followups, setFollowups] = useState(null);
    const [doctors, setDoctors] = useState([]);
    const [doctorFilter, setDoctorFilter] = useState("");
    const [showNew, setShowNew] = useState(params.get("new") === "1");
    const [saving, setSaving] = useState(false);
    const [notifying, setNotifying] = useState(null);

    const [patientQuery, setPatientQuery] = useState(params.get("pname") || "");
    const [patientResults, setPatientResults] = useState([]);
    const [selected, setSelected] = useState(params.get("pid") ? { id: params.get("pid"), name: params.get("pname") } : null);
    const [form, setForm] = useState({ date: today, time: "", reason: "", with_doctor_id: "" });

    const load = async () => {
        try {
            const [{ data: a }, { data: f }] = await Promise.all([
                api.get("/appointments", { params: { date, doctor_id: doctorFilter } }),
                api.get("/followups", { params: { days: 7 } }),
            ]);
            setAppts(a);
            setFollowups(f);
        } catch (e) {
            toast.error(fmtErr(e));
        }
    };

    const loadMonth = async () => {
        const y = month.getFullYear();
        const m = month.getMonth();
        const from = `${y}-${String(m + 1).padStart(2, "0")}-01`;
        const last = new Date(y, m + 1, 0).getDate();
        const to = `${y}-${String(m + 1).padStart(2, "0")}-${last}`;
        try {
            const { data } = await api.get("/appointments", { params: { from_date: from, to_date: to, doctor_id: doctorFilter } });
            const map = {};
            data.forEach((a) => {
                if (!map[a.date]) map[a.date] = [];
                map[a.date].push(a);
            });
            setMonthAppts(map);
        } catch (e) { toast.error(fmtErr(e)); }
    };

    useEffect(() => { api.get("/team").then(({ data }) => setDoctors(data)).catch(() => {}); }, []);
    useEffect(() => { load(); }, [date, doctorFilter]);
    useEffect(() => { if (view === "calendar") loadMonth(); }, [view, month, doctorFilter]);

    useEffect(() => {
        if (!patientQuery.trim() || selected) { setPatientResults([]); return; }
        const t = setTimeout(async () => {
            try {
                const { data } = await api.get("/patients", { params: { search: patientQuery } });
                setPatientResults(data);
            } catch { setPatientResults([]); }
        }, 250);
        return () => clearTimeout(t);
    }, [patientQuery, selected]);

    const create = async (e) => {
        e.preventDefault();
        if (!selected) { toast.error("Select a patient"); return; }
        setSaving(true);
        try {
            await api.post("/appointments", { patient_id: selected.id, ...form });
            toast.success(`Appointment booked for ${selected.name}`);
            setShowNew(false);
            setParams({});
            setSelected(null);
            setPatientQuery("");
            setForm({ date: today, time: "", reason: "", with_doctor_id: "" });
            load();
            if (view === "calendar") loadMonth();
        } catch (err) {
            toast.error(fmtErr(err));
        } finally {
            setSaving(false);
        }
    };

    const notify = async (a) => {
        setNotifying(a.id);
        try {
            const { data } = await api.post(`/appointments/${a.id}/notify`);
            notifyToast(data);
            load();
        } catch (e) {
            toast.error(fmtErr(e));
        } finally {
            setNotifying(null);
        }
    };

    const remind = async (v) => {
        setNotifying(v.id);
        try {
            const { data } = await api.post(`/visits/${v.id}/remind`);
            notifyToast(data);
        } catch (e) {
            toast.error(fmtErr(e));
        } finally {
            setNotifying(null);
        }
    };

    const setStatus = async (a, status) => {
        try {
            await api.put(`/appointments/${a.id}`, { status });
            toast.success(`Marked ${status}`);
            load();
        } catch (e) { toast.error(fmtErr(e)); }
    };

    const remove = async (a) => {
        if (!window.confirm(`Delete appointment for ${a.patient_name} on ${a.date} ${a.time}?`)) return;
        try {
            await api.delete(`/appointments/${a.id}`);
            toast.success("Appointment deleted");
            load();
        } catch (e) { toast.error(fmtErr(e)); }
    };

    const shiftMonth = (delta) => {
        const d = new Date(month);
        d.setMonth(d.getMonth() + delta);
        setMonth(d);
    };

    const renderCalendar = () => {
        const y = month.getFullYear();
        const m = month.getMonth();
        const firstDow = new Date(y, m, 1).getDay();
        const daysInMonth = new Date(y, m + 1, 0).getDate();
        const cells = [];
        for (let i = 0; i < firstDow; i++) cells.push(null);
        for (let d = 1; d <= daysInMonth; d++) cells.push(d);
        return (
            <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden" data-testid="appointments-calendar">
                <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
                    <h3 className="font-heading font-semibold text-slate-900">{month.toLocaleString("en-IN", { month: "long", year: "numeric" })}</h3>
                    <div className="flex gap-1.5">
                        <Button size="sm" variant="outline" data-testid="cal-prev-btn" onClick={() => shiftMonth(-1)}><ChevronLeft className="h-4 w-4" /></Button>
                        <Button size="sm" variant="outline" data-testid="cal-today-btn" onClick={() => { const d = new Date(); d.setDate(1); setMonth(d); }} className="text-xs">Today</Button>
                        <Button size="sm" variant="outline" data-testid="cal-next-btn" onClick={() => shiftMonth(1)}><ChevronRight className="h-4 w-4" /></Button>
                    </div>
                </div>
                <div className="grid grid-cols-7 text-center text-[11px] font-semibold uppercase tracking-wider text-slate-400 border-b border-slate-100">
                    {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => <div key={d} className="py-2">{d}</div>)}
                </div>
                <div className="grid grid-cols-7">
                    {cells.map((d, i) => {
                        if (d === null) return <div key={`x-${i}`} className="min-h-[92px] border-b border-r border-slate-50 bg-slate-50/40" />;
                        const ds = `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
                        const dayAppts = monthAppts[ds] || [];
                        const isToday = ds === today;
                        return (
                            <button
                                key={ds}
                                data-testid={`cal-day-${ds}`}
                                onClick={() => { setDate(ds); setView("list"); }}
                                className={`min-h-[92px] border-b border-r border-slate-100 p-1.5 text-left align-top hover:bg-[#FFF4EC]/60 transition-colors duration-100 ${isToday ? "bg-[#FFF4EC]" : "bg-white"}`}
                            >
                                <span className={`inline-flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold ${isToday ? "bg-[#E06D1F] text-white" : "text-slate-600"}`}>{d}</span>
                                <div className="mt-1 space-y-0.5">
                                    {dayAppts.slice(0, 2).map((a) => (
                                        <p key={a.id} className={`truncate rounded px-1 py-0.5 text-[10px] font-medium ${a.status === "completed" ? "bg-emerald-50 text-emerald-700" : a.status === "cancelled" ? "bg-slate-100 text-slate-400 line-through" : "bg-blue-50 text-blue-700"}`}>
                                            {a.time} {a.patient_name}
                                        </p>
                                    ))}
                                    {dayAppts.length > 2 && <p className="text-[10px] font-semibold text-[#C2410C] px-1">+{dayAppts.length - 2} more</p>}
                                </div>
                            </button>
                        );
                    })}
                </div>
            </div>
        );
    };

    return (
        <div className="space-y-5 animate-fade-up" data-testid="appointments-page">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">Appointments</h1>
                    <p className="mt-1 text-base text-slate-500">Schedule visits and send patient reminders</p>
                </div>
                <div className="flex gap-2">
                    <ImportDialog module="appointments" label="Import" onDone={load} />
                    <div className="flex rounded-lg border border-slate-200 bg-white overflow-hidden" data-testid="view-toggle">
                        <button data-testid="view-list-btn" onClick={() => setView("list")} className={`px-3 py-2 text-xs font-semibold flex items-center gap-1.5 ${view === "list" ? "bg-[#E06D1F] text-white" : "text-slate-600 hover:bg-slate-50"}`}><List className="h-3.5 w-3.5" /> List</button>
                        <button data-testid="view-calendar-btn" onClick={() => setView("calendar")} className={`px-3 py-2 text-xs font-semibold flex items-center gap-1.5 ${view === "calendar" ? "bg-[#E06D1F] text-white" : "text-slate-600 hover:bg-slate-50"}`}><LayoutGrid className="h-3.5 w-3.5" /> Calendar</button>
                    </div>
                    <Button data-testid="new-appointment-btn" onClick={() => setShowNew(true)} className="bg-[#E06D1F] hover:bg-[#B45309] text-white gap-2">
                        <Plus className="h-4 w-4" /> New Appointment
                    </Button>
                </div>
            </div>

            <div className="rounded-xl border border-slate-200 bg-white shadow-sm" data-testid="followups-section">
                <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
                    <BellRing className="h-4 w-4 text-[#E06D1F]" />
                    <h3 className="font-heading font-semibold text-slate-900">Follow-ups Due (next 7 days)</h3>
                    <span className="text-xs text-slate-500">{followups ? followups.length : ""}</span>
                </div>
                {!followups ? (
                    <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-[#E06D1F]" /></div>
                ) : followups.length === 0 ? (
                    <p className="px-5 py-6 text-sm text-slate-500" data-testid="no-followups">No follow-ups due. Set a follow-up date when saving a consultation.</p>
                ) : (
                    <div className="divide-y divide-slate-100">
                        {followups.map((v) => (
                            <div key={v.id} data-testid={`followup-card-${v.visit_no}`} className="flex flex-wrap items-center justify-between gap-3 px-5 py-3">
                                <div>
                                    <p className="text-sm font-medium text-slate-900">
                                        {v.patient_name} <span className="font-mono2 text-xs text-[#C2410C]">{v.patient_uid}</span>
                                        {v.overdue && <span className="ml-2 rounded-full bg-red-50 border border-red-200 px-2 py-0.5 text-[10px] font-semibold text-red-600" data-testid={`overdue-badge-${v.visit_no}`}>OVERDUE</span>}
                                    </p>
                                    <p className="text-xs text-slate-500">Follow-up: <b>{v.follow_up}</b> · {v.diagnosis || "Consultation"} · {v.patient_phone}</p>
                                </div>
                                <Button data-testid={`followup-remind-${v.visit_no}`} size="sm" variant="outline" onClick={() => remind(v)} disabled={notifying === v.id} className="gap-1.5">
                                    {notifying === v.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Send Reminder
                                </Button>
                                {v.patient_phone && (
                                    <a
                                        data-testid={`followup-whatsapp-${v.visit_no}`}
                                        href={`https://wa.me/91${v.patient_phone.replace(/\D/g, "").slice(-10)}?text=${encodeURIComponent(`Hello ${v.patient_name}, this is a follow-up reminder from ${user?.clinic_name || "Zen Hospital"}. ${user?.name || "Your doctor"} has asked you to review on ${v.follow_up}.`)}`}
                                        target="_blank" rel="noreferrer"
                                        className="inline-flex items-center gap-1.5 rounded-md border border-green-200 bg-green-50 px-2.5 py-1.5 text-xs font-medium text-green-700 hover:bg-green-100 transition-colors duration-150"
                                    ><MessageCircle className="h-3.5 w-3.5" /> WhatsApp</a>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {view === "calendar" ? renderCalendar() : (
                <div className="rounded-xl border border-slate-200 bg-white shadow-sm" data-testid="appointments-list-card">
                    <div className="px-5 py-4 border-b border-slate-100 flex flex-wrap items-center justify-between gap-3">
                        <h3 className="font-heading font-semibold text-slate-900 flex items-center gap-2"><Calendar className="h-4 w-4 text-[#E06D1F]" /> Schedule</h3>
                        <div className="flex gap-2">
                            <select data-testid="appointments-doctor-filter" value={doctorFilter} onChange={(e) => setDoctorFilter(e.target.value)} className="rounded-lg border border-slate-300 bg-white py-2 px-3 text-sm shadow-sm focus:border-orange-500 outline-none">
                                <option value="">All doctors</option>
                                {doctors.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                            </select>
                            <input
                                type="date"
                                data-testid="appointments-date-filter"
                                value={date}
                                onChange={(e) => setDate(e.target.value)}
                                className="rounded-lg border border-slate-300 bg-white py-2 px-3 text-sm shadow-sm focus:border-emerald-600 focus:ring-1 focus:ring-emerald-600 outline-none"
                            />
                        </div>
                    </div>
                    {!appts ? (
                        <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-[#E06D1F]" /></div>
                    ) : appts.length === 0 ? (
                        <p className="px-5 py-10 text-center text-sm text-slate-500" data-testid="no-appointments">No appointments on {date}.</p>
                    ) : (
                        <div className="divide-y divide-slate-100">
                            {appts.map((a) => (
                                <div key={a.id} data-testid={`appt-row-${a.id}`} className="flex flex-wrap items-center justify-between gap-3 px-5 py-3.5">
                                    <div className="flex items-center gap-4">
                                        <span className="flex items-center gap-1 text-sm font-mono2 font-medium text-slate-700"><Clock className="h-3.5 w-3.5 text-slate-400" />{a.time}</span>
                                        <div>
                                            <p className="text-sm font-medium text-slate-900">{a.patient_name} <span className="font-mono2 text-xs text-[#C2410C]">{a.patient_uid}</span></p>
                                            <p className="text-xs text-slate-500">{a.reason || "Consultation"}{a.with_doctor_name ? ` · ${a.with_doctor_name}` : ""} · {a.patient_phone}{a.patient_email ? ` · ${a.patient_email}` : ""}</p>
                                        </div>
                                        <span className={`rounded-full border px-2.5 py-0.5 text-[11px] font-semibold capitalize ${STATUS_STYLES[a.status]}`} data-testid={`appt-status-${a.id}`}>{a.status}</span>
                                    </div>
                                    <div className="flex gap-1.5">
                                        {a.patient_phone && (
                                            <a
                                                data-testid={`appt-whatsapp-${a.id}`}
                                                href={`https://wa.me/91${a.patient_phone.replace(/\D/g, "").slice(-10)}?text=${encodeURIComponent(`Hello ${a.patient_name}, your appointment with ${user?.name || "your doctor"} (${user?.clinic_name || "Zen Hospital"}) is confirmed for ${a.date} at ${a.time}.${a.reason ? ` Reason: ${a.reason}.` : ""}`)}`}
                                                target="_blank" rel="noreferrer"
                                                className="inline-flex items-center gap-1.5 rounded-md border border-green-200 bg-green-50 px-2.5 py-1.5 text-xs font-medium text-green-700 hover:bg-green-100 transition-colors duration-150"
                                            ><MessageCircle className="h-3.5 w-3.5" /> WhatsApp</a>
                                        )}
                                        <Button data-testid={`appt-notify-${a.id}`} size="sm" variant="outline" onClick={() => notify(a)} disabled={notifying === a.id} className="gap-1.5">
                                            {notifying === a.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Notify
                                        </Button>
                                        {a.status === "scheduled" && (
                                            <Button data-testid={`appt-complete-${a.id}`} size="sm" variant="outline" onClick={() => setStatus(a, "completed")} className="gap-1 text-emerald-700 border-emerald-200 hover:bg-emerald-50">
                                                <CheckCircle className="h-3.5 w-3.5" /> Done
                                            </Button>
                                        )}
                                        {a.status === "scheduled" && (
                                            <Button data-testid={`appt-cancel-${a.id}`} size="sm" variant="outline" onClick={() => setStatus(a, "cancelled")} className="gap-1 text-amber-700 border-amber-200 hover:bg-amber-50">
                                                <XCircle className="h-3.5 w-3.5" /> Cancel
                                            </Button>
                                        )}
                                        <Button data-testid={`appt-delete-${a.id}`} size="sm" variant="ghost" onClick={() => remove(a)} className="text-slate-400 hover:text-red-600">
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            <Dialog open={showNew} onOpenChange={(o) => { setShowNew(o); if (!o) setParams({}); }}>
                <DialogContent className="max-w-lg" data-testid="add-appointment-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-heading">Book Appointment</DialogTitle>
                        <DialogDescription>Pick a patient, date and time. You can notify the patient after booking.</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={create} className="space-y-4">
                        <div className="space-y-1.5 relative">
                            <Label>Patient *</Label>
                            {selected ? (
                                <div className="flex items-center justify-between rounded-lg border border-emerald-200 bg-[#FFF4EC] px-3 py-2.5" data-testid="selected-patient-chip">
                                    <span className="text-sm font-medium text-slate-900">{selected.name}</span>
                                    <button type="button" data-testid="clear-selected-patient" onClick={() => { setSelected(null); setPatientQuery(""); }} className="text-xs text-slate-500 hover:text-red-600">Change</button>
                                </div>
                            ) : (
                                <>
                                    <div className="relative">
                                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                                        <input
                                            data-testid="appt-patient-search-input"
                                            value={patientQuery}
                                            onChange={(e) => setPatientQuery(e.target.value)}
                                            placeholder="Search patient by name, phone or ID..."
                                            className="w-full rounded-lg border border-slate-300 bg-white py-2.5 pl-9 pr-3.5 text-sm shadow-sm focus:border-emerald-600 focus:ring-1 focus:ring-emerald-600 outline-none placeholder:text-slate-400"
                                        />
                                    </div>
                                    {patientResults.length > 0 && (
                                        <div className="absolute z-20 mt-1 w-full rounded-lg border border-slate-200 bg-white shadow-lg max-h-52 overflow-y-auto" data-testid="appt-patient-results">
                                            {patientResults.map((p) => (
                                                <button key={p.id} type="button" data-testid={`appt-patient-option-${p.patient_uid}`} onClick={() => { setSelected(p); setPatientResults([]); }} className="w-full text-left px-4 py-2.5 hover:bg-[#FFF4EC] border-b border-slate-100 last:border-0">
                                                    <span className="text-sm font-medium text-slate-900">{p.name}</span>
                                                    <span className="ml-2 text-xs text-slate-500 font-mono2">{p.patient_uid}</span>
                                                    <span className="ml-2 text-xs text-slate-400">{p.phone}</span>
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-1.5">
                                <Label>Date *</Label>
                                <Input data-testid="appt-date-input" type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
                            </div>
                            <div className="space-y-1.5">
                                <Label>Time *</Label>
                                <Input data-testid="appt-time-input" type="time" required value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} />
                            </div>
                        </div>
                        <div className="space-y-1.5">
                            <Label>Doctor</Label>
                            <select data-testid="appt-doctor-select" value={form.with_doctor_id} onChange={(e) => setForm({ ...form, with_doctor_id: e.target.value })} className="w-full rounded-lg border border-slate-300 bg-white py-2.5 px-3.5 text-sm shadow-sm focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none">
                                <option value="">Any doctor</option>
                                {doctors.map((d) => <option key={d.id} value={d.id}>{d.name}{d.specialization ? ` — ${d.specialization}` : ""}</option>)}
                            </select>
                        </div>
                        <div className="space-y-1.5">
                            <Label>Reason</Label>
                            <Input data-testid="appt-reason-input" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="Follow-up, fever review, dressing..." />
                        </div>
                        <Button data-testid="appt-submit-btn" type="submit" disabled={saving || !selected} className="w-full bg-[#E06D1F] hover:bg-[#B45309] text-white">
                            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Book Appointment
                        </Button>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}
